import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  StyleSheet,
  Platform,
  useWindowDimensions,
} from 'react-native';
import CalendarPicker from 'react-native-calendar-picker';

export type DateRange = { startDate?: string; endDate?: string };

type DateRangeModalProps = {
  visible: boolean;
  setVisible: (val: boolean) => void;
  dateRange?: DateRange;
  onApply?: (range: DateRange) => void;
};

export const DateRangeModal = ({
  visible,
  setVisible,
  dateRange,
  onApply,
}: DateRangeModalProps) => {
  const [pendingRange, setPendingRange] = useState<DateRange>({});

  useEffect(() => {
    if (visible) setPendingRange(dateRange || {});
  }, [visible, dateRange]);

  const handleDateChange = (date: Date | null, type: 'START_DATE' | 'END_DATE') => {
    if (!date) return; // Guard against null

    const formatted = date.toISOString().split('T')[0];

    if (type === 'START_DATE') {
      setPendingRange({ startDate: formatted, endDate: undefined });
    } else {
      setPendingRange(prev => ({
        ...prev,
        endDate: formatted,
      }));
    }
  };

  const handleApply = () => {
    if (onApply) onApply(pendingRange);
    setVisible(false);
  };

  const handleCancelOrReset = () => {
    if (pendingRange.startDate || pendingRange.endDate) setPendingRange({});
    else setVisible(false);
  };

  const selectedStartDate = pendingRange.startDate ? new Date(pendingRange.startDate) : undefined;
  const selectedEndDate = pendingRange.endDate ? new Date(pendingRange.endDate) : undefined;
  const { width } = useWindowDimensions();
  const isMobileWeb = Platform.OS === 'web' && width <= 768;

  const isMobile = isMobileWeb || Platform.OS !== 'web';

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={() => setVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.modalContent, { width: isMobile ? '90%' : 500 }]}>
          <Text style={styles.title}>Select Date Range</Text>

          <CalendarPicker
            startFromMonday={true}
            allowRangeSelection={true}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            onDateChange={handleDateChange}
            todayBackgroundColor="#FCEABB"
            todayTextStyle={{ color: '#000' }}
            selectedDayColor="#E89823"
            selectedDayTextColor="#fff"
            width={isMobile ? 350 : 450}
          />

          <View style={styles.buttonContainer}>
            <TouchableOpacity style={styles.cancelButton} onPress={handleCancelOrReset}>
              <Text style={[styles.buttonText, styles.cancelText]}>
                {pendingRange.startDate || pendingRange.endDate ? 'Reset' : 'Cancel'}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.applyButton, { backgroundColor: '#E89823' }]}
              onPress={handleApply}
            >
              <Text style={[styles.buttonText, styles.applyText]}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  modalOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.5)',
  },
  modalContent: {
    backgroundColor: '#fff',
    borderRadius: 12,
    paddingVertical: 20,
    paddingHorizontal: 10,
    width: '90%',
    maxHeight: '80%',
  },
  title: { fontSize: 16, fontWeight: '600', textAlign: 'center', marginBottom: 15 },
  buttonContainer: {
    flexDirection: 'row',
    marginTop: 15,
    justifyContent: 'space-between',
    paddingHorizontal: 10,
  },
  cancelButton: {
    flex: 1,
    borderWidth: 1,
    borderColor: '#0442FF26',
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
    marginRight: 8,
  },
  applyButton: {
    flex: 1,
    paddingVertical: 12,
    alignItems: 'center',
    borderRadius: 8,
    marginLeft: 8,
  },
  buttonText: { fontWeight: '700', fontSize: 16 },
  applyText: { color: '#fff' },
  cancelText: { color: '#000' },
});
